/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useContext } from 'react';
import errorReporter from '@app/errorReporter';
import { AuthContext } from 'contexts';
import { TransferState } from '@bb/users';
import { message } from 'components';
import { transferUser, cancelOrDenyTransfer } from 'api/UserController';

import FormContext from '../../FormContext';
import IncomingPendingTransferAlert from './IncomingPendingTransferAlert';
import IncomingSuggestedTransferAlert from './IncomingSuggestedTransferAlert';

import OutgoingPendingTransferAlert from './OutgoingPendingTransferAlert';
import UnverifiedAlert from './UnverifiedAlert';
import NoAccessAlert from './NoAccessAlert';

interface PageAlertProps {
  handleSave: () => Promise<void>;
  navigateBack: () => void;
  navigateToTransfersList: () => void;
  requestTransfer: () => Promise<void>;
}
const PageAlert = ({
  navigateBack,
  navigateToTransfersList,
  requestTransfer,
}: PageAlertProps) => {
  const { logout } = useContext(AuthContext);

  const { isProfile, transferId, outgoingTransferId, alert, setWorking } =
    useContext(FormContext);

  const cancelTransfer = async () => {
    try {
      setWorking(true);
      await cancelOrDenyTransfer(transferId, TransferState.Cancelled);
      message.success('Transfer request cancelled');
      navigateToTransfersList();
    } catch (err) {
      let msg = err;
      if (err.isAxiosError && err.response?.data?.message) {
        msg = err.response.data.message;
      }
      errorReporter.error(`Failed to cancel transfer: ${msg}`);
      message.error(`Failed to cancel transfer: ${msg}`);
    } finally {
      setWorking(false);
    }
  };
  const acceptTransfer = async () => {
    try {
      setWorking(true);
      await transferUser({ outgoingTransferId });
      message.success('Transfer request accepted');
      if (isProfile) {
        // User is accepting their own transfer, and should be logged out
        logout();
      } else {
        navigateToTransfersList();
      }
    } catch (err) {
      let msg = err;
      if (err.isAxiosError && err.response?.data?.message) {
        msg = err.response.data.message;
      }
      errorReporter.error(`Failed to accept transfer: ${msg}`);
      message.error(`Failed to accept transfer: ${msg}`);
    } finally {
      setWorking(false);
    }
  };
  const denyTransfer = async () => {
    try {
      setWorking(true);
      await cancelOrDenyTransfer(outgoingTransferId, TransferState.Denied);
      message.success('Transfer request denied');
      if (isProfile) {
        navigateBack();
      } else {
        navigateToTransfersList();
      }
    } catch (err) {
      let msg = err;
      if (err.isAxiosError && err.response?.data?.message) {
        msg = err.response.data.message;
      }
      errorReporter.error(`Failed to deny transfer: ${msg}`);
      message.error(`Failed to deny transfer: ${msg}`);
    } finally {
      setWorking(false);
    }
  };

  if (!alert.length) return null;

  return (
    <>
      {alert.map(v => {
        switch (v) {
          case 'unverified':
            return <UnverifiedAlert key={v} />;

          case 'incomingTransfer':
            return (
              <IncomingPendingTransferAlert
                key={v}
                cancelTransfer={cancelTransfer}
              />
            );

          case 'outgoingTransfer':
            return (
              <OutgoingPendingTransferAlert
                key={v}
                acceptTransfer={acceptTransfer}
                denyTransfer={denyTransfer}
              />
            );

          case 'suggestTransfer':
            return (
              <IncomingSuggestedTransferAlert
                key={v}
                requestTransfer={requestTransfer}
              />
            );

          case 'noAccess':
            return <NoAccessAlert />;
          default:
            return null;
        }
      })}
    </>
  );
};

export default PageAlert;
